import React, { useEffect, useState } from 'react'

import CircularProgress from './components/CircularProgress'

import { inboundV2Service } from '@/services'
import {
  InboundProgressScheam,
  MarkProgress
} from '@/services/InboundV2Service'

const MonitorInbound: React.FC = () => {
  const [progressData, setProgressData] = useState<InboundProgressScheam[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastRefreshTime, setLastRefreshTime] = useState<string>('')

  // 30秒自动刷新
  useEffect(() => {
    let interval: NodeJS.Timeout

    if (autoRefresh) {
      interval = setInterval(() => {
        loadProgressData(false) // 静默刷新，不显示loading
      }, 30000) // 30秒
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [autoRefresh])

  useEffect(() => {
    loadProgressData()
  }, [])

  const loadProgressData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true)
      }

      // const data = await monitorService.getInboundProgress()
      const { success, data } = await inboundV2Service.monitor()
      if (success && data) {
        setProgressData(data)
        // 如果当前没有选中的标签页，或选中的索引超出范围，自动选择第一个
        if (activeTab >= data.length) {
          setActiveTab(0)
          setLastRefreshTime(new Date().toLocaleTimeString('zh-CN'))
        }
      }
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  const handleRefresh = () => {
    loadProgressData(true)
  }

  const calculateMasterBillProgress = (cargoMarksProgress: MarkProgress[]) => {
    if (!cargoMarksProgress || cargoMarksProgress.length === 0) return 0

    const totalBoxes = cargoMarksProgress.reduce(
      (sum, mark) => sum + mark.summary.total,
      0
    )
    const palletizedBoxes = cargoMarksProgress.reduce(
      (sum, mark) => sum + mark.summary.palletized,
      0
    )

    return totalBoxes > 0 ? Math.round((palletizedBoxes / totalBoxes) * 100) : 0
  }

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500'
    if (percentage >= 30) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getProgressTextColor = (percentage: number): string => {
    if (percentage >= 80) return 'text-green-600'
    if (percentage >= 30) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading && progressData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">加载入库进度...</span>
      </div>
    )
  }

  if (progressData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-gray-500 mb-4">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7"
            />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            暂无入库进度
          </h3>
          <p>当前没有正在入库的主提单</p>
        </div>
        <button
          onClick={handleRefresh}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          刷新数据
        </button>
      </div>
    )
  }

  const currentMasterBill = progressData[activeTab]

  return (
    <div className="bg-gray-50">
      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* 页面标题和控制栏 */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">入库进度监控</h1>
              <p className="text-sm text-gray-600 mt-1">
                最后更新: {lastRefreshTime}
                {autoRefresh && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                    自动刷新
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-600">30秒自动刷新</span>
              </label>

              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                刷新
              </button>
            </div>
          </div>

          {/* Tab标签页 */}
          {progressData.length > 1 && (
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6">
                  {progressData.map((masterBill, index) => (
                    <button
                      key={masterBill.masterBillId}
                      onClick={() => setActiveTab(index)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === index
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <span>{masterBill.masterBlNumber}</span>
                        {(() => {
                          const allMarks =
                            masterBill.cargoMarksGroup?.flatMap(
                              (group) => group.marks
                            ) || []
                          const progress = calculateMasterBillProgress(allMarks)
                          return (
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                progress >= 80
                                  ? 'bg-green-100 text-green-800'
                                  : progress >= 30
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {progress}%
                            </span>
                          )
                        })()}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {/* 主提单进度概览 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentMasterBill.masterBlNumber}
              </h2>
              {(() => {
                const allMarks =
                  currentMasterBill.cargoMarksGroup?.flatMap(
                    (group) => group.marks
                  ) || []
                const completedMarks = allMarks.filter(
                  (mark) => mark.isCompleted
                ).length
                const totalMarks = allMarks.length
                const totalPallets =
                  currentMasterBill.palletsGroup?.reduce(
                    (sum, pg) => sum + pg.pallets.length,
                    0
                  ) || 0
                const progress = calculateMasterBillProgress(allMarks)

                return (
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-600">
                      唛头: {completedMarks}/{totalMarks}
                    </span>
                    <span className="text-gray-600">托盘: {totalPallets}</span>
                    <span
                      className={`font-bold text-lg ${getProgressTextColor(
                        progress
                      )}`}
                    >
                      {progress}%
                    </span>
                  </div>
                )
              })()}
            </div>

            {/* 总体进度条 */}
            <div className="mb-6">
              {(() => {
                const allMarks =
                  currentMasterBill.cargoMarksGroup?.flatMap(
                    (group) => group.marks
                  ) || []
                const progress = calculateMasterBillProgress(allMarks)

                return (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full transition-all duration-500 ${getProgressColor(
                          progress
                        )}`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </>
                )
              })()}
            </div>

            {/* 状态统计卡片 */}
            {(() => {
              const allMarks =
                currentMasterBill.cargoMarksGroup?.flatMap(
                  (group) => group.marks
                ) || []
              const total = allMarks.reduce(
                (sum, mark) => sum + mark.summary.total,
                0
              )
              const scanned = allMarks.reduce(
                (sum, mark) => sum + mark.summary.scanned,
                0
              )
              const palletized = allMarks.reduce(
                (sum, mark) => sum + mark.summary.palletized,
                0
              )
              const pending = total - palletized

              return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {pending}
                    </div>
                    <div className="text-sm text-red-600">待处理</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {scanned}
                    </div>
                    <div className="text-sm text-blue-600">已扫描</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {palletized}
                    </div>
                    <div className="text-sm text-green-600">已打托</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-800">
                      {total}
                    </div>
                    <div className="text-sm text-gray-600">总箱数</div>
                  </div>
                </div>
              )
            })()}
          </div>

          {/* 分提单详细进度 */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                分提单进度详情
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {currentMasterBill.cargoMarksGroup.map((houseBill) => {
                const progress = calculateMasterBillProgress(houseBill.marks)
                const completedMarks = houseBill.marks.filter(
                  (mark) => mark.isCompleted
                ).length
                return (
                  <div key={houseBill.houseBillId} className="p-6">
                    {/* 分提单标题 */}
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-gray-900">
                        {houseBill.houseBlNumber}
                      </h4>
                      {(() => {
                        const palletCount =
                          currentMasterBill.palletsGroup.find(
                            (pg) => pg.houseBillId === houseBill.houseBillId
                          )?.pallets.length || 0

                        return (
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-gray-600">
                              唛头: {completedMarks}/{houseBill.marks.length}
                            </span>
                            <span className="text-gray-600">
                              托盘: {palletCount}
                            </span>
                            <span
                              className={`font-bold ${getProgressTextColor(
                                progress
                              )}`}
                            >
                              {progress}%
                            </span>
                          </div>
                        )
                      })()}
                    </div>

                    {/* 分提单进度条 */}
                    <div className="mb-4">
                      {(() => {
                        const total = houseBill.marks.reduce(
                          (sum, mark) => sum + mark.summary.total,
                          0
                        )
                        const scanned = houseBill.marks.reduce(
                          (sum, mark) => sum + mark.summary.scanned,
                          0
                        )
                        const palletized = houseBill.marks.reduce(
                          (sum, mark) => sum + mark.summary.palletized,
                          0
                        )
                        const pending = total - palletized

                        return (
                          <>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(
                                  progress
                                )}`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-600 mt-1">
                              <span>待处理: {pending}</span>
                              <span>已扫描: {scanned}</span>
                              <span>已打托: {palletized}</span>
                              <span>总计: {total}</span>
                            </div>
                          </>
                        )
                      })()}
                    </div>

                    {/* Marks进度展示 */}
                    {houseBill.marks && houseBill.marks.length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-4"></div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {houseBill.marks.map((mark) => {
                            const percentage =
                              mark.summary.total > 0
                                ? Math.round(
                                    (mark.summary.palletized /
                                      mark.summary.total) *
                                      100
                                  )
                                : 0
                            const status = mark.isCompleted
                              ? 'completed'
                              : mark.summary.palletized > 0
                              ? 'in_progress'
                              : 'pending'

                            return (
                              <div
                                key={mark.markNumber}
                                className="flex items-center p-3 bg-gray-50 rounded-lg"
                              >
                                <CircularProgress
                                  percentage={percentage}
                                  status={status}
                                  size={50}
                                  strokeWidth={3}
                                />
                                <div className="ml-3 flex-1">
                                  <div className="text-xs font-medium text-gray-800 truncate">
                                    {mark.markNumber}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {mark.summary.palletized}/
                                    {mark.summary.total}箱
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MonitorInbound
